import React, { useRef, useCallback } from 'react';
import { useDetailsPanel } from '@/contexts/DetailsPanelContext';
import { useLocation } from 'react-router-dom';
import { useEventListener } from '@/hooks/useEventListener';
import { DetailsPanel } from './index';

interface DetailsPanelWrapperProps {
  isRoom: boolean;
  isZone: boolean;
}

export const DetailsPanelWrapper: React.FC<DetailsPanelWrapperProps> = ({ isRoom, isZone }) => {
  const { isOpen, close } = useDetailsPanel();
  const { pathname } = useLocation();
  const panelRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback(
    (event: Event) => {
      const target = event.target as Element;
      const panelElement = panelRef.current;
      const gearButton = document.querySelector('[data-id="showHideDetails"]');

      const isClickInsidePanel = panelElement?.contains(target);
      const isClickOnGearButton = gearButton?.contains(target) || gearButton === target;

      // Check if click is on dropdown portals. Class selectors are the
      // public DOM contract each library exposes; role selectors are a
      // defensive fallback for any Select dropdown that emits ARIA roles
      // without a stable wrapper class — currently no known consumer needs
      // them, kept until the next primitive audit confirms.
      const isClickOnDropdown =
        target.closest('[data-radix-popper-content-wrapper]') || // Radix popover/tooltip/dropdown-menu portals
        target.closest('.ant-select-dropdown') || // Ant Select dropdown
        target.closest('.ant-picker-dropdown') || // Ant DatePicker outer popup
        target.closest('.ant-picker-panel') || // Ant DatePicker calendar (covers .ant-picker-cell etc. via closest())
        target.closest('.ant-dropdown') || // Ant Dropdown/Menu popup
        target.closest('[role="listbox"]') || // Defensive fallback (see comment above)
        target.closest('[role="option"]'); // Defensive fallback (see comment above)

      if (!isClickInsidePanel && !isClickOnGearButton && !isClickOnDropdown) {
        close();
      }
    },
    [close],
  );

  // Only attach the event listener when the panel is open
  useEventListener('mousedown', isOpen ? handleClickOutside : () => {}, document, {
    capture: true,
  });

  if (!isOpen) {
    return null;
  }

  return (
    <div ref={panelRef} className="right-0 top-0 h-full z-20 shadow-lg bg-surface">
      <DetailsPanel isRoom={isRoom} isZone={isZone} isStudio={pathname === '/studio'} />
    </div>
  );
};
